/**
 * Vendor Import Wizard — bulk create vendors from Excel/CSV.
 * Steps: Upload → Preview → Commit → Done
 */
import React, { useRef, useState } from 'react';
import { Upload, Download, CheckCircle2, XCircle, AlertTriangle, Loader2, X, FileText } from 'lucide-react';
import { apiPost, BACKEND_URL } from '../../lib/api';
import { toast } from 'sonner';

const STEPS = ['Upload', 'Preview', 'Done'];

export default function VendorImportWizard({ onClose, onDone }) {
  const [step, setStep]           = useState(0);
  const [file, setFile]           = useState(null);
  const [parsing, setParsing]     = useState(false);
  const [preview, setPreview]     = useState(null);  // { rows, total, valid, invalid }
  const [committing, setCommitting] = useState(false);
  const [result, setResult]       = useState(null);  // { created, skipped, errors }
  const inputRef = useRef();

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setPreview(null); }
  };

  const parse = async () => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await apiPost('/vendors/bulk-import/parse', fd, {
        headers: { 'Content-Type': undefined }, // let browser set multipart boundary
      });
      setPreview(r);
      setStep(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Parse failed');
    } finally { setParsing(false); }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const validRows = preview.rows.filter(r => r.status === 'ok');
      const r = await apiPost('/vendors/bulk-import/commit', { rows: validRows });
      setResult(r);
      setStep(2);
      toast.success(`${r.created} vendors imported`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setCommitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" data-testid="vendor-import-wizard">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="font-display font-bold text-lg">Vendor Bulk Import</h2>
            <p className="text-xs text-slate-500 mt-0.5">Upload Excel or CSV to add multiple vendors at once</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        {/* Step indicator */}
        <div className="flex px-6 py-3 gap-6 border-b border-slate-100">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1.5">
              <span className={`w-5 h-5 rounded-full text-[11px] flex items-center justify-center font-bold ${
                i < step ? 'bg-emerald-500 text-white' : i === step ? 'bg-[#3b4872] text-white' : 'bg-slate-100 text-slate-400'
              }`}>{i < step ? '✓' : i + 1}</span>
              <span className={`text-xs font-display font-bold ${i === step ? 'text-[#3b4872]' : 'text-slate-400'}`}>{s}</span>
              {i < STEPS.length - 1 && <span className="text-slate-200 ml-2">›</span>}
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Step 0: Upload */}
          {step === 0 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <FileText size={16} className="shrink-0" />
                <span>Download the template, fill it in, then upload here. Fields marked * are required.</span>
                <a
                  href={`${BACKEND_URL}/api/vendors/bulk-import/template`}
                  className="ml-auto shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-[#3b4872] text-white rounded-lg text-xs font-bold whitespace-nowrap"
                  data-testid="vendor-import-download-template"
                >
                  <Download size={12} /> Template
                </a>
              </div>

              <div
                onClick={() => inputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                  file ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-[#3b4872] bg-slate-50'
                }`}
                data-testid="vendor-import-drop"
              >
                <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" onChange={pickFile} hidden />
                {file ? (
                  <>
                    <CheckCircle2 size={32} className="text-emerald-500 mx-auto mb-2" />
                    <p className="font-display font-bold text-slate-700">{file.name}</p>
                    <p className="text-xs text-slate-400 mt-1">{(file.size / 1024).toFixed(1)} KB — click to change</p>
                  </>
                ) : (
                  <>
                    <Upload size={32} className="text-slate-300 mx-auto mb-2" />
                    <p className="font-display font-bold text-slate-500">Click to upload Excel or CSV</p>
                    <p className="text-xs text-slate-400 mt-1">.xlsx · .xls · .csv — max 500 rows</p>
                  </>
                )}
              </div>

              <div className="text-xs text-slate-500 space-y-1 bg-slate-50 rounded-lg p-3">
                <p className="font-semibold">Required columns:</p>
                <p className="font-mono">name, vendor_type, contact_person, phone, address, gstin, pan, bank_name, bank_account, bank_ifsc</p>
                <p className="mt-1">vendor_type options: material_supplier · subcontractor · consultant · equipment_hire · amc_provider · labour_contractor · transport</p>
              </div>
            </div>
          )}

          {/* Step 1: Preview */}
          {step === 1 && preview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="font-display font-bold text-2xl">{preview.total}</div>
                  <div className="text-xs text-slate-500">Rows found</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <div className="font-display font-bold text-2xl text-emerald-700">{preview.valid}</div>
                  <div className="text-xs text-emerald-600">Ready to import</div>
                </div>
                <div className={`rounded-lg p-3 text-center ${preview.invalid > 0 ? 'bg-rose-50' : 'bg-slate-50'}`}>
                  <div className={`font-display font-bold text-2xl ${preview.invalid > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{preview.invalid}</div>
                  <div className="text-xs text-slate-500">Rows with errors</div>
                </div>
              </div>

              {/* Row table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left w-8">#</th>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">GSTIN</th>
                      <th className="px-3 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.rows.slice(0, 50).map((r, i) => (
                      <tr key={i} className={r.status === 'error' ? 'bg-rose-50' : ''}>
                        <td className="px-3 py-1.5 text-slate-400">{r.row}</td>
                        <td className="px-3 py-1.5 font-medium">{r.data.name || '—'}</td>
                        <td className="px-3 py-1.5 text-slate-500">{r.data.vendor_type || '—'}</td>
                        <td className="px-3 py-1.5 font-mono text-slate-500">{r.data.gstin || '—'}</td>
                        <td className="px-3 py-1.5">
                          {r.status === 'ok'
                            ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={12} /> OK</span>
                            : <span className="text-rose-600 flex items-center gap-1" title={r.errors.join(', ')}>
                                <XCircle size={12} /> {r.errors[0]}
                              </span>
                          }
                        </td>
                      </tr>
                    ))}
                    {preview.rows.length > 50 && (
                      <tr><td colSpan={5} className="px-3 py-2 text-center text-slate-400">… and {preview.rows.length - 50} more rows</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {preview.invalid > 0 && (
                <p className="text-xs text-rose-600 flex items-center gap-1.5">
                  <AlertTriangle size={12} /> {preview.invalid} row(s) with errors will be skipped. Fix the Excel and re-upload to include them.
                </p>
              )}
            </div>
          )}

          {/* Step 2: Done */}
          {step === 2 && result && (
            <div className="flex flex-col items-center py-8 gap-4 text-center">
              <CheckCircle2 size={48} className="text-emerald-500" />
              <div>
                <p className="font-display font-bold text-xl text-slate-800">{result.created} vendors imported</p>
                {result.skipped > 0 && <p className="text-sm text-slate-500 mt-1">{result.skipped} skipped (duplicates or errors)</p>}
              </div>
              {result.errors?.length > 0 && (
                <ul className="text-xs text-rose-600 text-left mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {result.errors.map((e, i) => <li key={i}>• {e}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
          {step === 0 && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-lg">Cancel</button>
              <button
                onClick={parse} disabled={!file || parsing}
                className="px-4 py-2 text-sm font-bold bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                data-testid="vendor-import-parse-btn"
              >
                {parsing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                Preview Import
              </button>
            </>
          )}
          {step === 1 && (
            <>
              <button onClick={() => { setStep(0); setPreview(null); }} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-lg">
                Back
              </button>
              <button
                onClick={commit} disabled={committing || preview?.valid === 0}
                className="px-4 py-2 text-sm font-bold bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                data-testid="vendor-import-commit-btn"
              >
                {committing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Import {preview?.valid} Vendor{preview?.valid !== 1 ? 's' : ''}
              </button>
            </>
          )}
          {step === 2 && (
            <button onClick={onClose} className="px-4 py-2 text-sm font-bold bg-[#3b4872] text-white rounded-lg">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
